<template>
	<video ref="videoPlayer" class="video-js"></video>
</template>

<script>
import videojs from "video.js";
import axios from "axios";
import 'video.js/dist/video-js.min.css';

export default {
	name: "VideoPlayer",
	props: {
		initialVideoOptions: {
			type: Object,
			default() {
				return {};
			},
		},
		initialVideoData: {
			type: Object,
			default() {
				return {};
			},
		},
	},

	data() {
		return {
			player: null,
			options: this.initialVideoOptions,
			videoData: this.initialVideoData,
			last_saved_position: this.initialVideoData.start_position,
			sent_watch: null,
			save_request: null,
		};
	},

	watch: {
		initialVideoOptions: function (val) {
			this.player.src(val.sources);
		},
		initialVideoData: function (val) {
			this.videoData = val;
			this.last_saved_position = val.start_position;
			this.sent_watch = null;
			if (val.start_position > 0) {
				this.player.currentTime(val.start_position);
			}
		},
	},

	mounted() {
		this.player = videojs(this.$refs.videoPlayer, this.options);
		this.player.on("ready", this.onPlayerReady);
		this.player.on("timeupdate", this.onPlayerTimeUpdate);
		this.player.on("ended", this.onPlayerEnded);
		this.player.on("pause", this.onPlayerPause);
		document.addEventListener("visibilitychange", this.onVisibilityChange);
	},

	beforeUnmount() {
		document.removeEventListener("visibilitychange", this.onVisibilityChange);
		if (this.player) {
			this.player.dispose();
		}
	},

	methods: {
		onPlayerReady() {
			this.player.currentTime(this.videoData.start_position);
		},

		videoEndPos() {
			return this.videoData.end_pos || this.player.duration() * 0.9;
		},

		// Периодически сохраняем шагом 30 секунд медиа-времени, на паузе и при уходе
		// со вкладки — точную позицию. При скрытой вкладке запрос идёт через fetch
		// с keepalive, чтобы пережить выгрузку страницы.
		savePosition(position, keepalive = false) {
			// Порог сравниваем с фактической позицией плеера, а не с округлённой: прыжок за end_pos
			// не должен сохранять корзину ниже и спорить с watch в том же тике.
			if (position <= 0 || this.player.currentTime() >= this.videoEndPos() || position === this.last_saved_position) return;

			this.last_saved_position = position;
			this.save_request = axios({
				response_type: "json",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
					"X-CSRF-Token": document
						.querySelector('meta[name="csrf-token"]')
						.getAttribute("content"),
				},

				url: this.videoData.save_video_position_path,
				method: "POST",
				adapter: keepalive ? "fetch" : "xhr",
				fetchOptions: { keepalive },

				data: {
					utf8: this.utf8,
					saved_video_position: position,
				},
			})
				.then((response) => {
					this.videoData.start_position = position;
					this.emitter.emit("update_video", this.videoData);
				})
				.catch((errors) => {
					console.warn(errors);
				});
		},

		onPlayerPause() {
			this.savePosition(Math.floor(this.player.currentTime()));
		},

		onVisibilityChange() {
			if (document.hidden && this.player) {
				this.savePosition(Math.floor(this.player.currentTime()), true);
			}
		},

		onPlayerTimeUpdate() {
			let position = this.player.currentTime();
			let end_pos = this.videoEndPos();
			const bucket = Math.floor(position / 30) * 30;
			if (bucket !== Math.floor(this.last_saved_position / 30) * 30) {
				this.savePosition(bucket);
			}

			if (position > end_pos && !this.sent_watch) {
				// Флаг ставим до запроса, иначе до ответа каждый timeupdate шлёт новый watch.
				// watch! сбрасывает позицию в 0, поэтому ждём ответ на последнее сохранение.
				this.sent_watch = true;
				Promise.resolve(this.save_request).then(() =>
					axios({
						response_type: "json",
						headers: {
							"X-Requested-With": "XMLHttpRequest",
							"X-CSRF-Token": document
								.querySelector('meta[name="csrf-token"]')
								.getAttribute("content"),
						},

						url: this.videoData.watch_video_path,
						method: "POST",
					})
						.then((response) => {
							this.emitter.emit("update_current_lecture");
						})
						.catch((errors) => {
							console.warn(errors);
						})
				);
			}
		},

		onPlayerEnded() {
			this.emitter.emit("next_video");
		},
	},
};
</script>
